import * as React from "react";
import { useModal } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { useNavigate, MemoryRouter } from "react-router-dom";
import { RouteJobsModalContent, RouteModalTitleContent } from "../components/RouteJobsModal";

export function useRouteJobsModal() {
  return useRouteCardModal(useNavigate());
}

/** From inside another modal, which renders outside the router: the card's links reload the page. */
export function useRouteJobsModalOutsideRouter() {
  return useRouteCardModal();
}

function useRouteCardModal(navigate?: (to: string) => void) {
  const { openModal, closeModal } = useModal();

  const openRouteJobs = (
    route: ConnectProto.Route,
    options?: { onClose?: () => void }
  ) => {
    const idRef = { current: "" };

    idRef.current = openModal({
      size: "custom",
      width: { md: "64rem", "xl": "65svw" }, // lg:!max-w-5xl ≈ 64rem
      showClose: true,
      className: "max-h-[80svh] h-auto overflow-hidden flex flex-col !gap-0 bg-white",
      bodyClassName: "!p-0 flex flex-col flex-1 min-h-0 overflow-hidden",
      closeButtonTop: "top-2",
      body: (
        <MemoryRouter>
          <RouteJobsModalContent
            route={route}
            onClose={() => closeModal(idRef.current)}
            navigate={navigate}
            onCloseModal={options?.onClose}
          />
        </MemoryRouter>
      ),
    });

    return idRef.current;
  };

  const openRouteJobsWithNavigation = (
    routes: ConnectProto.Route[],
    initialRoute: ConnectProto.Route,
    options?: {
      onActiveItemChange?: (route: ConnectProto.Route) => void;
      onClose?: () => void;
    }
  ) => {
    const initialIndex = routes.findIndex((r) => r.id === initialRoute.id);
    const idRef = { current: "" };

    idRef.current = openModal({
      size: "custom",
      width: {  "xl": "65svw" }, // lg:!max-w-5xl ≈ 64rem
      showClose: true,
      className: "max-h-[80svh] h-auto overflow-hidden flex flex-col !gap-0 bg-white",
      bodyClassName: "!p-0 flex flex-col flex-1 min-h-0 overflow-hidden",
      headerClassName: "!p-0 !pr-12",
      closeButtonTop: "top-5",
      navigation: {
        items: routes,
        initialIndex: initialIndex >= 0 ? initialIndex : 0,
        getItemId: (route) => String(route.id),
        renderTitle: (route) => <RouteModalTitleContent route={route} />,
        renderBody: (route) => (
          <MemoryRouter>
            <RouteJobsModalContent
              route={route}
              onClose={() => closeModal(idRef.current)}
              navigate={navigate}
              onCloseModal={options?.onClose}
              isNavigationMode={true}
            />
          </MemoryRouter>
        ),
        onActiveItemChange: (route) => {
          options?.onActiveItemChange?.(route);
        },
      },
    });

    return idRef.current;
  };

  return { openRouteJobs, openRouteJobsWithNavigation };
}
